import { useState, useEffect, useCallback } from "react";
import { Play, Pause, Plus, Loader2, Search, X } from "lucide-react";
import { usePlayer } from "../contexts/PlayerContext";
import { useCart } from "../contexts/CartContext";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import LicenseModal from "./LicenseModal";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getInitialQuery() {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  return params.get("q") || "";
}

export default function BeatCatalog() {
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ genres: [], moods: [] });
  const [genre, setGenre] = useState("all");
  const [mood, setMood] = useState("all");
  const [sort, setSort] = useState("newest");
  const [query, setQuery] = useState(getInitialQuery());
  const [debouncedQuery, setDebouncedQuery] = useState(getInitialQuery());
  const [licenseModalBeat, setLicenseModalBeat] = useState(null);
  const { currentTrack, isPlaying, playTrack } = usePlayer();

  // Debounce search input + sync to URL
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(query.trim());
      const params = new URLSearchParams(window.location.search);
      if (query.trim()) {
        params.set("q", query.trim());
      } else {
        params.delete("q");
      }
      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : "") +
        window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const fetchBeats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort });
      if (genre !== "all") params.append("genre", genre);
      if (mood !== "all") params.append("mood", mood);
      if (debouncedQuery) params.append("q", debouncedQuery);
      const res = await axios.get(`${API}/beats?${params}`);
      setBeats(res.data.beats || []);
    } catch (err) {
      console.error("Failed to fetch beats:", err);
    } finally {
      setLoading(false);
    }
  }, [genre, mood, sort, debouncedQuery]);

  const fetchFilters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/filters`);
      setFilters(res.data);
    } catch (err) {
      console.error("Failed to fetch filters:", err);
    }
  }, []);

  useEffect(() => {
    fetchBeats();
  }, [fetchBeats]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  return (
    <section id="beats" data-testid="beat-catalog" className="py-24 px-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-12">
        <p className="text-xs tracking-[0.2em] uppercase text-gold mb-3">Catalog</p>
        <h2
          className="text-4xl sm:text-5xl tracking-tight font-medium text-[#EAEAEA] mb-4"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Premium Beats
        </h2>
        <p className="text-[#737373] text-base max-w-xl">
          Each beat is meticulously crafted. Listen, choose your license, and make it yours.
        </p>
      </div>

      {/* Search + Filters */}
      <div className="mb-6">
        <div className="relative max-w-xl">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none"
          />
          <input
            data-testid="beats-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title, slug, key, genre, mood…"
            className="w-full chrome-panel focus:border-gold/60 outline-none pl-10 pr-10 py-2.5 text-sm text-[#EAEAEA] placeholder:text-[#5A5A5A] tracking-wide rounded-lg transition-colors"
          />
          {query && (
            <button
              data-testid="beats-search-clear"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-gold transition-colors"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div data-testid="beat-filters" className="flex flex-wrap gap-3 mb-8">
        <Select value={genre} onValueChange={setGenre}>
          <SelectTrigger data-testid="genre-filter" className="w-[140px] chrome-panel text-[#A1A1A1] rounded-lg">
            <SelectValue placeholder="Genre" />
          </SelectTrigger>
          <SelectContent className="chrome-panel">
            <SelectItem value="all">All Genres</SelectItem>
            {filters.genres.map((g) => (
              <SelectItem key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={mood} onValueChange={setMood}>
          <SelectTrigger data-testid="mood-filter" className="w-[140px] chrome-panel text-[#A1A1A1] rounded-lg">
            <SelectValue placeholder="Mood" />
          </SelectTrigger>
          <SelectContent className="chrome-panel">
            <SelectItem value="all">All Moods</SelectItem>
            {filters.moods.map((m) => (
              <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="sort-select" className="w-[150px] chrome-panel text-[#A1A1A1] rounded-lg">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent className="chrome-panel">
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="popular">Popular</SelectItem>
            <SelectItem value="price_low">Price: Low</SelectItem>
            <SelectItem value="price_high">Price: High</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Beat List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-gold" size={32} />
        </div>
      ) : beats.length === 0 ? (
        <div data-testid="no-beats" className="text-center py-20 text-[#737373]">
          <p className="text-lg">No beats found. Try adjusting your filters.</p>
        </div>
      ) : (
        <div data-testid="beat-list" className="border-t border-[#1A1A1A]">
          {beats.map((beat, i) => (
            <BeatRow
              key={beat.id}
              beat={beat}
              index={i}
              isActive={currentTrack?.id === beat.id}
              isPlaying={currentTrack?.id === beat.id && isPlaying}
              onPlay={() => playTrack(beat)}
              onLicense={() => setLicenseModalBeat(beat)}
            />
          ))}
        </div>
      )}

      {/* License Modal */}
      {licenseModalBeat && (
        <LicenseModal
          beat={licenseModalBeat}
          onClose={() => setLicenseModalBeat(null)}
        />
      )}
    </section>
  );
}

function BeatRow({ beat, index, isActive, isPlaying, onPlay, onLicense }) {
  return (
    <div
      data-testid={`beat-row-${beat.id}`}
      className={`flex items-center gap-4 py-4 px-3 border-b border-[#1A1A1A] transition-colors cursor-pointer group animate-fade-slide-up ${
        isActive ? "bg-white/[0.03]" : "hover:bg-white/[0.02]"
      }`}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      {/* Play Button */}
      <button
        data-testid={`play-btn-${beat.id}`}
        onClick={onPlay}
        className={`flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full transition-all ${
          isActive
            ? "bg-gold text-black"
            : "bg-white/[0.05] text-[#A1A1A1] group-hover:bg-gold/20 group-hover:text-gold"
        }`}
      >
        {isPlaying ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
      </button>

      {/* Track Info */}
      <div className="flex-1 min-w-0">
        <h3
          className={`text-sm font-medium truncate ${isActive ? "text-gold" : "text-[#EAEAEA]"}`}
        >
          {beat.title}
        </h3>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-[#737373]">{beat.bpm} BPM</span>
          <span className="text-xs text-[#737373]">{beat.key}</span>
        </div>
      </div>

      {/* Tags */}
      <div className="hidden md:flex items-center gap-1.5 flex-shrink-0">
        {beat.genre.slice(0, 2).map((g) => (
          <Badge
            key={g}
            variant="outline"
            className="text-[10px] tracking-wider uppercase text-[#737373] border-[#1A1A1A] bg-black/50 px-2 py-0.5 rounded-sm"
          >
            {g}
          </Badge>
        ))}
        {beat.mood.slice(0, 1).map((m) => (
          <Badge
            key={m}
            variant="outline"
            className="text-[10px] tracking-wider uppercase text-[#737373] border-[#1A1A1A] bg-black/50 px-2 py-0.5 rounded-sm"
          >
            {m}
          </Badge>
        ))}
      </div>

      {/* Price */}
      <div className="flex-shrink-0 text-right mr-2">
        <span className="text-sm text-gold font-medium">${beat.pricing.standard}</span>
      </div>

      {/* Add to Cart */}
      <button
        data-testid={`license-btn-${beat.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onLicense();
        }}
        className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-white/[0.05] text-[#A1A1A1] hover:bg-gold hover:text-black rounded-sm transition-all"
      >
        <Plus size={16} />
      </button>
    </div>
  );
}
